/**
 * Composable for managing admin impersonation state.
 * Allows A55 staff to impersonate merchant accounts.
 */

const STORAGE_KEY = 'a55-admin-impersonation'

interface ImpersonationState {
  customerId: string | null
  customerName: string | null
  customerEmail: string | null
  isTestAccount: boolean
}

interface StoredImpersonation {
  customerId: string
  customerName: string
  customerEmail: string
  isTestAccount: boolean
}

function loadFromStorage(): ImpersonationState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as StoredImpersonation
      return {
        customerId: parsed.customerId,
        customerName: parsed.customerName,
        customerEmail: parsed.customerEmail,
        isTestAccount: parsed.isTestAccount ?? false
      }
    }
  } catch (e) {
    console.warn('Failed to load impersonation state:', e)
  }

  return { customerId: null, customerName: null, customerEmail: null, isTestAccount: false }
}

function saveToStorage(state: ImpersonationState): void {
  if (state.customerId) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      customerId: state.customerId,
      customerName: state.customerName,
      customerEmail: state.customerEmail,
      isTestAccount: state.isTestAccount
    }))
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }
}

// Singleton state using useState for shared storage
export function useImpersonation() {
  const state = useState<ImpersonationState>('impersonation', () => loadFromStorage())
  const isSwitching = useState('impersonation-switching', () => false)

  // isInitializing is only used during account switching now (not for page restore)
  const isInitializing = useState('impersonation-initializing', () => false)

  const isImpersonating = computed(() => !!state.value.customerId)

  // Show overlay for both switching and initial loading
  const isLoading = computed(() => isSwitching.value || isInitializing.value)

  const impersonatedCustomer = computed(() => ({
    customerId: state.value.customerId,
    customerName: state.value.customerName,
    customerEmail: state.value.customerEmail,
    isTestAccount: state.value.isTestAccount
  }))

  /**
   * Switch to a new customer account.
   * Sets switching state, updates customer, refreshes all data.
   */
  async function setCustomer(customer: {
    entity_uuid: string
    name: string
    email: string
    is_test_account?: boolean
  }): Promise<void> {
    const previousCustomerId = state.value.customerId
    const isSameAccount = previousCustomerId === customer.entity_uuid

    // Don't do anything if selecting the same account
    if (isSameAccount) return

    // Show overlay for any account selection (first or switch)
    isSwitching.value = true

    state.value = {
      customerId: customer.entity_uuid,
      customerName: customer.name,
      customerEmail: customer.email,
      isTestAccount: customer.is_test_account ?? false
    }
    saveToStorage(state.value)

    // Refresh all cached data for the new account
    try {
      await refreshNuxtData()
    } finally {
      isSwitching.value = false
    }
  }

  function clearCustomer(): void {
    state.value = {
      customerId: null,
      customerName: null,
      customerEmail: null,
      isTestAccount: false
    }
    saveToStorage(state.value)
  }

  return {
    isImpersonating,
    isSwitching: computed(() => isSwitching.value),
    isInitializing: computed(() => isInitializing.value),
    isLoading,
    impersonatedCustomer,
    setCustomer,
    clearCustomer
  }
}
